import { ArrowRight, Filter, Link2, Plus, Sparkles } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';

interface EmptyStateProps {
  onCreateRule: () => void;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ onCreateRule }) => {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-gradient-to-b from-white to-slate-50/50 px-8 py-16">
      {/* Icon illustration */}
      <div className="relative mb-8">
        {/* Outer ring with subtle animation hint */}
        <div className="absolute inset-0 -m-3 rounded-full bg-blue-100/50" />
        <div className="absolute inset-0 -m-1.5 rounded-full bg-blue-50" />

        {/* Main icon container */}
        <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-100">
          <Link2 className="h-7 w-7 text-blue-500" />
        </div>

        {/* Decorative orbiting dots */}
        <div className="absolute -left-6 top-1/2 flex -translate-y-1/2 items-center gap-1">
          <div className="h-1.5 w-1.5 rounded-full bg-slate-300" />
          <div className="h-1 w-1 rounded-full bg-slate-200" />
        </div>
        <div className="absolute -right-6 top-1/2 flex -translate-y-1/2 items-center gap-1">
          <div className="h-1 w-1 rounded-full bg-slate-200" />
          <div className="h-1.5 w-1.5 rounded-full bg-slate-300" />
        </div>
      </div>

      <h3 className="text-xl font-semibold tracking-tight text-slate-900">
        No linking rules yet
      </h3>
      <p className="mt-3 max-w-sm text-center text-sm leading-relaxed text-slate-500">
        Create your first rule to automatically connect Filevine tasks with
        internal tasks based on names, types, and other criteria.
      </p>

      <Button onClick={onCreateRule} className="mt-8 gap-2 px-6">
        <Plus className="h-4 w-4" />
        Create Your First Rule
      </Button>

      {/* Feature highlights */}
      <div className="mt-12 grid max-w-xl grid-cols-3 gap-8">
        <FeatureHighlight
          icon={Filter}
          title="Smart Filters"
          description="Match by name, type, or status"
        />
        <FeatureHighlight
          icon={ArrowRight}
          title="Auto Linking"
          description="Connect tasks automatically"
        />
        <FeatureHighlight
          icon={Sparkles}
          title="Live Preview"
          description="Test rules before saving"
        />
      </div>
    </div>
  );
};

interface FeatureHighlightProps {
  icon: React.ElementType;
  title: string;
  description: string;
}

const FeatureHighlight: React.FC<FeatureHighlightProps> = ({
  icon: Icon,
  title,
  description,
}) => (
  <div className="flex flex-col items-center text-center">
    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
      <Icon className="h-4 w-4 text-slate-500" />
    </div>
    <div className="mt-3 text-sm font-medium text-slate-700">{title}</div>
    <p className="mt-1 text-xs text-slate-400">{description}</p>
  </div>
);
